import React, { useEffect, useMemo, useState } from 'react';
import {
  Loader2, RefreshCw, Search, Users as UsersIcon,
  Ban, Unlock, Trash2, AlertTriangle, UserCheck,
} from 'lucide-react';
import {
  adminUsersService,
  type AdminUser,
  type FeatureUsage,
  type RegistrationPolicy,
  type UserLoginEvent,
  type UserSummary,
} from '@/services/adminUsersService';
import { useAuth } from '@/auth/AuthContext';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

// ── formatting helpers ──────────────────────────────────────────────

const fmtDateTime = (s: string | null | undefined): string =>
  s ? new Date(s).toLocaleString() : '—';

const fmtDate = (s: string | null | undefined): string =>
  s ? new Date(s).toLocaleDateString() : '—';

/** Trim a User-Agent to a readable device hint. */
function deviceHint(ua: string | null): string {
  if (!ua) return '—';
  return ua.length > 64 ? `${ua.slice(0, 64)}…` : ua;
}

function OnlineDot({ online }: { online: boolean }) {
  return (
    <span
      title={online ? 'Online' : 'Offline'}
      className={`inline-block h-2 w-2 rounded-full ${
        online ? 'bg-emerald-500' : 'bg-muted-foreground/30'
      }`}
    />
  );
}

function outcomeVariant(outcome: string): 'secondary' | 'destructive' | 'outline' {
  return outcome === 'Success' ? 'secondary' : 'destructive';
}

// ── registration policy card (SuperAdmin) ───────────────────────────

function RegistrationPolicyCard() {
  const [policy, setPolicy] = useState<RegistrationPolicy | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    adminUsersService.getRegistrationPolicy()
      .then((p) => { if (!cancelled) setPolicy(p); })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const toggle = async () => {
    if (!policy) return;
    setBusy(true);
    setError(null);
    try {
      setPolicy(await adminUsersService.setRegistrationEnabled(!policy.registrationEnabled));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update');
    } finally {
      setBusy(false);
    }
  };

  const enabled = policy?.registrationEnabled ?? true;

  return (
    <div className="mb-4 rounded-lg border bg-card p-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-sm font-medium">
            <UserCheck className="h-4 w-4 text-primary" />
            New-account registration
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {loading
              ? 'Loading…'
              : enabled
                ? 'Open — anyone can create an account from the sign-up page.'
                : 'Closed — the sign-up page is disabled and new registrations are rejected.'}
            {policy?.updatedAt && !loading && (
              <> · last changed {fmtDateTime(policy.updatedAt)}</>
            )}
          </p>
          {error && <p className="mt-1 text-xs text-destructive">{error}</p>}
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={enabled ? 'secondary' : 'destructive'}>
            {loading ? '…' : enabled ? 'Enabled' : 'Disabled'}
          </Badge>
          <Button
            size="sm"
            variant={enabled ? 'destructive' : 'default'}
            disabled={loading || busy || !policy}
            onClick={() => void toggle()}
          >
            {busy && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
            {enabled ? 'Disable registration' : 'Enable registration'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── per-user detail dialog ──────────────────────────────────────────

function UserDetailDialog({
  user,
  isSuperAdmin,
  onClose,
  onUserUpdated,
  onUserDeleted,
}: {
  user: AdminUser | null;
  isSuperAdmin: boolean;
  onClose: () => void;
  onUserUpdated: (u: AdminUser) => void;
  onUserDeleted: (userId: string) => void;
}) {
  const [summary, setSummary] = useState<UserSummary | null>(null);
  const [usage, setUsage] = useState<FeatureUsage[]>([]);
  const [logins, setLogins] = useState<UserLoginEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Admin-action state.
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  useEffect(() => {
    // Reset per-action state on every target change (including dialog close).
    // The dialog component stays mounted across opens, so a `busy` flag left
    // over from a previous user's action — e.g. a request that never settled —
    // would otherwise render THIS user's buttons stuck-spinning before any
    // click. Done before the !user guard so it also clears on close.
    setBusy(false);
    setActionError(null);
    setConfirmingDelete(false);
    if (!user) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setSummary(null);
    setUsage([]);
    setLogins([]);
    Promise.all([
      adminUsersService.summary(user.id),
      adminUsersService.usage(user.id),
      adminUsersService.logins(user.id),
    ])
      .then(([s, u, l]) => {
        if (cancelled) return;
        setSummary(s);
        setUsage(u);
        setLogins(l);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load user detail');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  const maxUsage = useMemo(
    () => usage.reduce((m, f) => Math.max(m, f.count), 0),
    [usage],
  );

  const toggleLockout = async () => {
    if (!user) return;
    setBusy(true);
    setActionError(null);
    try {
      onUserUpdated(await adminUsersService.setLockout(user.id, !user.isLockedOut));
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Failed to update account');
    } finally {
      setBusy(false);
    }
  };

  const deleteUser = async (cascade: boolean) => {
    if (!user) return;
    setBusy(true);
    setActionError(null);
    try {
      await adminUsersService.deleteUser(user.id, cascade);
      onUserDeleted(user.id);
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Failed to delete account');
      setBusy(false); // keep dialog open so the error is visible
    }
  };

  return (
    <Dialog open={user !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        {user && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <OnlineDot online={user.online} />
                {user.email}
              </DialogTitle>
            </DialogHeader>

            {/* identity strip */}
            <div className="flex flex-wrap gap-2 text-xs">
              {user.roles.map((r) => (
                <Badge key={r} variant="outline">{r}</Badge>
              ))}
              <Badge variant="secondary">{user.tier}</Badge>
              {user.isLockedOut && <Badge variant="destructive">Disabled</Badge>}
              {user.online && (
                <Badge variant="secondary">
                  {user.connectionCount} connection{user.connectionCount === 1 ? '' : 's'}
                </Badge>
              )}
            </div>

            <div className="mt-2 grid grid-cols-2 gap-x-6 gap-y-1 text-sm sm:grid-cols-3">
              <Field label="Registered" value={fmtDate(user.createdAt)} />
              <Field label="Last login" value={fmtDateTime(user.lastLoginAt)} />
              <Field label="Last login IP" value={user.lastLoginIp ?? '—'} />
              <Field label="Last seen" value={fmtDateTime(user.lastSeenAt)} />
            </div>

            {loading && (
              <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading detail…
              </div>
            )}
            {error && <p className="py-4 text-sm text-destructive">{error}</p>}

            {!loading && !error && (
              <>
                {/* activity summary */}
                <Section title="Activity">
                  <div className="grid grid-cols-3 gap-3">
                    <Stat label="Strategies" value={summary?.strategyCount ?? 0} />
                    <Stat label="Backtest runs" value={summary?.runCount ?? 0} />
                    <Stat label="Scanners" value={summary?.scannerCount ?? 0} />
                  </div>
                  <p className="mt-2 text-xs text-muted-foreground">
                    Last activity: {fmtDateTime(summary?.lastActivityAt)}
                  </p>
                </Section>

                {/* per-feature usage */}
                <Section title="Feature usage (last 30 days)">
                  {usage.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No tracked usage.</p>
                  ) : (
                    <div className="space-y-1.5">
                      {usage.map((f) => (
                        <div key={f.feature} className="flex items-center gap-3 text-sm">
                          <span className="w-28 shrink-0">{f.feature}</span>
                          <div className="h-2 flex-1 rounded bg-muted">
                            <div
                              className="h-2 rounded bg-primary"
                              style={{ width: `${maxUsage > 0 ? (f.count / maxUsage) * 100 : 0}%` }}
                            />
                          </div>
                          <span className="w-14 shrink-0 text-right tabular-nums text-muted-foreground">
                            {f.count}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </Section>

                {/* login history */}
                <Section title="Login history">
                  {logins.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No login events.</p>
                  ) : (
                    <table className="w-full text-xs">
                      <thead className="text-muted-foreground">
                        <tr className="text-left">
                          <th className="py-1 pr-3 font-medium">When</th>
                          <th className="py-1 pr-3 font-medium">Event</th>
                          <th className="py-1 pr-3 font-medium">IP</th>
                          <th className="py-1 font-medium">Device</th>
                        </tr>
                      </thead>
                      <tbody>
                        {logins.map((e, i) => (
                          <tr key={`${e.occurredAt}-${i}`} className="border-t border-border/50">
                            <td className="py-1 pr-3 whitespace-nowrap">{fmtDateTime(e.occurredAt)}</td>
                            <td className="py-1 pr-3">
                              <Badge variant={outcomeVariant(e.outcome)}>
                                {e.kind} · {e.outcome}
                              </Badge>
                            </td>
                            <td className="py-1 pr-3 whitespace-nowrap">{e.ipAddress ?? '—'}</td>
                            <td className="py-1 text-muted-foreground">{deviceHint(e.userAgent)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </Section>
              </>
            )}

            {/* admin actions */}
            <Section title="Admin actions">
              {actionError && <p className="mb-2 text-sm text-destructive">{actionError}</p>}
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant={user.isLockedOut ? 'default' : 'outline'}
                  disabled={busy}
                  onClick={() => void toggleLockout()}
                >
                  {busy
                    ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                    : user.isLockedOut
                      ? <Unlock className="mr-1.5 h-3.5 w-3.5" />
                      : <Ban className="mr-1.5 h-3.5 w-3.5" />}
                  {user.isLockedOut ? 'Enable account' : 'Disable account'}
                </Button>
                {isSuperAdmin && !confirmingDelete && (
                  <Button
                    size="sm"
                    variant="destructive"
                    disabled={busy}
                    onClick={() => { setConfirmingDelete(true); setActionError(null); }}
                  >
                    <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                    Delete account…
                  </Button>
                )}
              </div>

              {isSuperAdmin && confirmingDelete && (
                <div className="mt-3 rounded-md border border-destructive/40 bg-destructive/5 p-3">
                  <div className="flex items-center gap-1.5 text-sm font-medium text-destructive">
                    <AlertTriangle className="h-4 w-4" />
                    Delete {user.email}?
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Choose how much to remove. Both options delete the user record and
                    its login history and cannot be undone.
                  </p>
                  <div className="mt-2.5 flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      variant="destructive"
                      disabled={busy}
                      onClick={() => void deleteUser(false)}
                    >
                      {busy && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
                      Delete account only
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      disabled={busy}
                      onClick={() => void deleteUser(true)}
                      title="Also deletes every strategy, run, scanner and setting this user owns"
                    >
                      {busy && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
                      Delete account + all their data
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={busy}
                      onClick={() => setConfirmingDelete(false)}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </Section>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <span className="text-muted-foreground">{label}: </span>
      <span className="text-foreground">{value}</span>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mt-4">
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {title}
      </h3>
      {children}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg border bg-card p-3 text-center">
      <div className="text-xl font-semibold tabular-nums">{value}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  );
}

// ── page ────────────────────────────────────────────────────────────

export const UserManagementPage: React.FC = () => {
  const { user: currentUser } = useAuth();
  const isSuperAdmin = currentUser?.roles?.includes('SuperAdmin') ?? false;

  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [onlineOnly, setOnlineOnly] = useState(false);
  const [selected, setSelected] = useState<AdminUser | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      setUsers(await adminUsersService.list());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  // Replace one user in the list (and the open dialog) after a lockout change.
  const handleUserUpdated = (u: AdminUser) => {
    setUsers((prev) => prev.map((x) => (x.id === u.id ? u : x)));
    setSelected((cur) => (cur && cur.id === u.id ? u : cur));
  };

  // Drop a deleted user from the list and close the dialog.
  const handleUserDeleted = (userId: string) => {
    setUsers((prev) => prev.filter((x) => x.id !== userId));
    setSelected(null);
  };

  const onlineCount = useMemo(() => users.filter((u) => u.online).length, [users]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users
      .filter((u) => !onlineOnly || u.online)
      .filter((u) => q === '' || u.email.toLowerCase().includes(q));
  }, [users, search, onlineOnly]);

  return (
    <div className="h-full overflow-y-auto p-4">
      {/* header */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <UsersIcon className="h-5 w-5 text-primary" />
          <h1 className="text-lg font-semibold">User Management</h1>
          <span className="text-sm text-muted-foreground">
            {users.length} user{users.length === 1 ? '' : 's'} · {onlineCount} online
          </span>
        </div>
        <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
          <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* registration policy — SuperAdmin only */}
      {isSuperAdmin && <RegistrationPolicyCard />}

      {/* filters */}
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by email…"
            className="h-8 w-64 rounded-md border bg-background pl-8 pr-2 text-sm outline-none focus:ring-1 focus:ring-primary"
          />
        </div>
        <label className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <input
            type="checkbox"
            checked={onlineOnly}
            onChange={(e) => setOnlineOnly(e.target.checked)}
          />
          Online only
        </label>
      </div>

      {/* states */}
      {loading && (
        <div className="flex items-center gap-2 py-10 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading users…
        </div>
      )}
      {error && <p className="py-6 text-sm text-destructive">{error}</p>}

      {/* table */}
      {!loading && !error && (
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-muted-foreground">
              <tr className="text-left">
                <th className="w-8 py-2 pl-3" />
                <th className="py-2 pr-3 font-medium">Email</th>
                <th className="py-2 pr-3 font-medium">Roles</th>
                <th className="py-2 pr-3 font-medium">Tier</th>
                <th className="py-2 pr-3 font-medium">Registered</th>
                <th className="py-2 pr-3 font-medium">Last login</th>
                <th className="py-2 pr-3 font-medium">IP</th>
                <th className="py-2 pr-3 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => (
                <tr
                  key={u.id}
                  onClick={() => setSelected(u)}
                  className="cursor-pointer border-t hover:bg-muted/50"
                >
                  <td className="py-2 pl-3"><OnlineDot online={u.online} /></td>
                  <td className="py-2 pr-3 font-medium">{u.email}</td>
                  <td className="py-2 pr-3">
                    <div className="flex flex-wrap gap-1">
                      {u.roles.map((r) => (
                        <Badge key={r} variant="outline">{r}</Badge>
                      ))}
                    </div>
                  </td>
                  <td className="py-2 pr-3 text-muted-foreground">{u.tier}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{fmtDate(u.createdAt)}</td>
                  <td className="py-2 pr-3 text-muted-foreground whitespace-nowrap">
                    {fmtDateTime(u.lastLoginAt)}
                  </td>
                  <td className="py-2 pr-3 text-muted-foreground">{u.lastLoginIp ?? '—'}</td>
                  <td className="py-2 pr-3">
                    {u.isLockedOut
                      ? <Badge variant="destructive">Disabled</Badge>
                      : <span className="text-muted-foreground">—</span>}
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={8} className="py-8 text-center text-sm text-muted-foreground">
                    No users match.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <UserDetailDialog
        user={selected}
        isSuperAdmin={isSuperAdmin}
        onClose={() => setSelected(null)}
        onUserUpdated={handleUserUpdated}
        onUserDeleted={handleUserDeleted}
      />
    </div>
  );
};

export default UserManagementPage;
